import React, { FunctionComponent } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import FolderIcon from '@mui/icons-material/Folder';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QuizIcon from '@mui/icons-material/Quiz';
import { Box, IconButton, Tooltip } from '@mui/material';

import { copyToClipboard } from '@paragon/client-tools';
import { enqueueSuccess } from '@paragon/notification-tools';

import {
  downloadComponentDesignFiles,
  downloadMergedPdfs,
  downloadMiscellaneousFiles,
  downloadSeparatedPdfs,
} from '../../actions/downloads';
import { getSelectedDocument } from '../../selectors/documents';

const borderSx = { width: '100%', m: 'auto' } as const;
const buttonSx = { width: '100%', m: 'auto', borderRadius: 0 } as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

const DownloadPdfs: FunctionComponent<Props> = (props) => {
  const onCopyClick = () => {
    copyToClipboard(props.documentUrl);
    props.enqueueSuccess('Successfully copied link to clipboard!');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-evenly',
        width: '100%',
        border: '1px solid',
        borderColor: 'backContrast.light',
      }}
    >
      <Box sx={borderSx}>
        <Tooltip
          disableInteractive
          disableFocusListener
          id="downloadSeparatedPdfs"
          title="Download all PDFs as separate documents"
        >
          <IconButton key="separated" onClick={props.downloadSeparatedPdfs} sx={buttonSx}>
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={borderSx}>
        <Tooltip
          disableInteractive
          disableFocusListener
          id="downloadMergedPdf"
          title="Download an unsecured merged PDF with my annotations"
        >
          <IconButton key="merged" onClick={props.downloadMergedPdfs} sx={buttonSx}>
            <LibraryBooksIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={borderSx}>
        <Tooltip
          disableInteractive
          disableFocusListener
          id="downloadMiscellaneousFiles"
          title="Download miscellaneous files"
        >
          <IconButton key="miscellaneous" onClick={props.downloadMiscellaneousFiles} sx={buttonSx}>
            <QuizIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={borderSx}>
        <Tooltip
          disableInteractive
          disableFocusListener
          id="downloadComponentDesign"
          title="Download all files for the selected component"
        >
          <IconButton key="componentDesign" onClick={props.downloadComponentDesigns} sx={buttonSx}>
            <FolderIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={borderSx}>
        <Tooltip disableInteractive disableFocusListener id="getPdfUrl" title="Copy PDF URL to Clipboard">
          <IconButton key="pdfUrl" onClick={() => onCopyClick()} sx={buttonSx}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

function mapStateToProps(state: any) {
  const selectedDocument = getSelectedDocument(state);
  const documentUrl = selectedDocument ? selectedDocument.url : '';

  return {
    documentUrl,
  };
}

const actionCreators = {
  downloadSeparatedPdfs,
  downloadMergedPdfs,
  downloadMiscellaneousFiles,
  downloadComponentDesigns: downloadComponentDesignFiles,
  enqueueSuccess,
};

export default connect(mapStateToProps, actionCreators)(DownloadPdfs);
